import React, {useCallback, useContext, useMemo, useState} from 'react';
import classnames from '@src/helpers/classnames';
import CustomCheckbox from '@src/components/Fields/CustomCheckbox';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import Translate from '@src/components/Translate/Translate';
import app from '@src/helpers/app';
import MessageReceiver from '@src/components/Chat/MessageReceiver/MessageReceiver';
import AttachmentsPreviews from '@src/components/Chat/AttachmentsPreviews/AttachmentsPreviews';

const VoidFunction = () => void 0;

type TButtonElementProps = React.ComponentProps<
        Exclude<Required<React.ComponentProps<typeof ConfirmableButton>>['buttonElement'], string>
> & { children: React.ReactNode };

function BaseButtonElement(props: TButtonElementProps & { baseClassName?: string }) {
    const {onClick, innerRef, children, disabled, baseClassName, className, ...rest} = props;

    const restProps = {...rest};
    ['type', 'onClick'].forEach(key => {
        if (key in restProps) {
            delete (restProps as any)[key];
        }
    });

    return <div
            {...restProps as any}
            className={classnames(baseClassName, className)}
            onClick={disabled ? undefined : onClick}
            ref={innerRef as any}
    >{children}</div>;
}

function ButtonElement(props: TButtonElementProps) {
    return <BaseButtonElement {...props} baseClassName="border rounded py-1 px-3 border-danger text-danger"/>;
}

function DisabledButtonElement(props: TButtonElementProps) {
    return <BaseButtonElement {...props} baseClassName="py-1 px-3 text-secondary" disabled/>;
}

function ActiveAndDisabledButtonElement(props: TButtonElementProps) {
    return <BaseButtonElement {...props} baseClassName="py-1 px-3 text-danger" disabled/>;
}

export default function TabsControlsWrapper(props: {
    className?: string,
    product: IOrderProductModel,
    children: React.ReactNode,
    onChange?: (newState: boolean) => any,
}) {
    const {product} = props;

    const {locale} = useContext(LocaleContext);
    const canMarkAsIgnored = usePermissionGranted('orders@can-mark-order-product-as-ignored-by-order-price');
    const canUnMarkAsIgnored = usePermissionGranted('orders@can-unmark-order-product-as-ignored-by-order-price');

    const baseChecked = product.has_to_be_ignored_by_order_price === true;
    const [isLoading, setIsLoading] = useState(false);
    const [forceChecked, setForceChecked] = useState<boolean | undefined>();
    const checked = forceChecked ?? baseChecked;
    const disabled = (
            checked && !canMarkAsIgnored ||
            !checked && !canUnMarkAsIgnored
    );

    const [attachments, setAttachments] = useState<File[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [onDrop, onDelete] = useMemo(() => {
        const onDrop: React.ComponentProps<typeof MessageReceiver>['onDrop'] = (attachments) => {
            setAttachments(list => [...list, ...attachments]);
        };

        const onDelete: React.ComponentProps<typeof AttachmentsPreviews>['onDelete'] = (index) => {
            setAttachments(list => {
                list.splice(index, 1);

                return [...list];
            });
        };

        return [onDrop, onDelete];
    }, []);

    const baseOnChange = props.onChange;
    const onChange = useCallback(() => {
        setErrorMessage(null);
        if (isLoading || disabled) {
            return;
        }

        setIsLoading(true);
        const newState = !checked;

        app('http').route('order-products.mark-as-ignored-by-order-price', {
            id: String(product.id),
        }, {
            json: {
                'controller-name': 'OrderProducts',
                state: newState,
                reason: {message, attachments}
            }
        }).then(() => {
            setForceChecked(newState);
            if (baseOnChange) {
                baseOnChange(newState);
            }
            setMessage('');
            setAttachments([]);
        }).catch(err => {
            setErrorMessage(err?.message ?? 'Something went wrong');
        }).finally(() => {
            setIsLoading(false);
        });
    }, [attachments, baseOnChange, checked, disabled, isLoading, message, product.id]);

    if (!product?.id || disabled && !checked) {
        return null;
    }

    return <div className={classnames(props.className, 'd-flex', {'align-items-center': disabled})}>
        {props.children}

        <div className="ms-auto">
            <ConfirmableButton
                    type="button"
                    onClick={onChange}
                    buttonElement={
                        disabled ?
                                (checked ? ActiveAndDisabledButtonElement : DisabledButtonElement) :
                                ButtonElement
                    }
                    popoverBody={<>
                        <Translate
                                tag="div"
                                message={`OrderForm.products.mark-as-ignored-by-price.are-you-sure-you-want-${checked ? 'unmark' : 'mark'}`}
                                className="mb-3"
                        />

                        <MessageReceiver
                                defaultValue={message}
                                onInput={setMessage}
                                onDrop={onDrop}
                                placeholder="OrderForm.products.mark-as-ignored-by-price.enter-reason"
                                filesPreviewSlot={
                                    <AttachmentsPreviews attachment={attachments} onDelete={onDelete}/>
                                }
                                isDisabled={disabled}
                        />

                        {
                            errorMessage ?
                                    <Translate
                                            tag="div"
                                            message={errorMessage}
                                            className="mt-3 alert alert-danger py-2 px-1"
                                    /> :
                                    null
                        }
                    </>}
                    disabled={disabled}
            >
                <CustomCheckbox
                        id={`checklist-item`}
                        label={trans('OrderForm.products.mark-as-ignored-by-price.button-label', locale)}
                        checkedValue={true}
                        uncheckedValue={false}
                        value={checked}
                        onChange={VoidFunction}
                        isLoading={isLoading}
                        disabled={disabled}
                />
            </ConfirmableButton>
        </div>
    </div>;
}
